<template>
  <section id="dashboard">
    <!-- 1. HERO CARD (Debtia gradient) -->
    <div class="hero-card animate-fade-up">
      <div class="hero-noise" />
      <div class="hero-inner">
        <div class="hero-left">
          <p class="hero-greeting">
            {{ $t('DEBTIA_HOME_WELCOME_BACK') }}
          </p>
          <h3 class="hero-name">
            {{ userName }}
          </h3>
          <div class="hero-meta">
            <span class="hero-meta-item">
              <feather-icon
                icon="CalendarIcon"
                size="14"
                class="mr-25"
              />
              {{ todayDate }}
            </span>
          </div>
        </div>
        <div class="hero-right">
          <small class="hero-label">{{ $t('DEBTIA_HOME_TOTAL_REMAINING') }}</small>
          <div class="hero-amount restgaeld-glow-wrap">
            {{ formatCurrency(animatedTotal) }}
          </div>
          <small class="hero-sublabel">
            {{ $t('DEBTIA_HOME_ACROSS_CASES', { count: activePausedCasesCount }) }}
          </small>
        </div>
      </div>
    </div>

    <!-- 2. KPI CARDS -->
    <b-row class="dashboard-row kpi-row">
      <b-col
        v-for="(kpi, idx) in kpiCards"
        :key="kpi.key"
        sm="6"
        lg="3"
      >
        <div
          class="kpi-card animate-fade-up"
          :style="{ animationDelay: `${0.08 + idx * 0.06}s` }"
        >
          <div class="kpi-icon-wrap">
            <b-avatar
              size="40"
              :variant="`light-${kpi.color}`"
            >
              <feather-icon
                :icon="kpi.icon"
                size="18"
              />
            </b-avatar>
          </div>
          <div class="kpi-info">
            <h3 class="kpi-value">
              {{ kpi.isCurrency ? formatCurrency(kpi.animated) : Math.round(kpi.animated) }}
            </h3>
            <span class="kpi-label">{{ kpi.label }}</span>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- 3. CHARTS ROW -->
    <b-row class="dashboard-row">
      <!-- Status Distribution (Donut) -->
      <b-col
        lg="5"
      >
        <div
          class="chart-card animate-fade-up"
          style="animation-delay: 0.35s"
        >
          <div class="chart-card-header">
            <h6 class="chart-title">
              <feather-icon
                icon="PieChartIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('DEBTIA_HOME_CHART_STATUS') }}
            </h6>
          </div>
          <div
            v-if="casesData.length > 0"
            class="chart-card-body"
          >
            <vue-apex-charts
              type="donut"
              height="280"
              :options="donutOptions"
              :series="donutSeries"
            />
          </div>
          <div
            v-else
            class="chart-empty"
          >
            <feather-icon
              icon="PieChartIcon"
              size="32"
              class="mb-75"
            />
            <span>{{ $t('DEBTIA_HOME_NO_CASES_YET') }}</span>
          </div>
        </div>
      </b-col>

      <!-- Cases Over Time (Area) -->
      <b-col
        lg="7"
      >
        <div
          class="chart-card animate-fade-up"
          style="animation-delay: 0.4s"
        >
          <div class="chart-card-header">
            <h6 class="chart-title">
              <feather-icon
                icon="TrendingUpIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('DEBTIA_HOME_CHART_OVER_TIME') }}
            </h6>
          </div>
          <div
            v-if="monthlyCategories.length > 0"
            class="chart-card-body"
          >
            <vue-apex-charts
              type="area"
              height="260"
              :options="areaOptions"
              :series="areaSeries"
            />
          </div>
          <div
            v-else
            class="chart-empty"
          >
            <feather-icon
              icon="TrendingUpIcon"
              size="32"
              class="mb-75"
            />
            <span>{{ $t('DEBTIA_HOME_NO_DATA_YET') }}</span>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- 4. LISTS ROW -->
    <b-row class="dashboard-row">
      <!-- Recent Cases -->
      <b-col
        lg="6"
      >
        <div
          class="list-card animate-fade-up"
          style="animation-delay: 0.45s"
        >
          <div class="list-card-header">
            <h6 class="list-title">
              <feather-icon
                icon="ClockIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('DEBTIA_HOME_RECENT_CASES') }}
            </h6>
            <router-link
              to="/cases"
              class="list-see-all"
            >
              {{ $t('DEBTIA_COMMON_SEE_ALL') }}
              <feather-icon
                icon="ArrowRightIcon"
                size="14"
                class="ml-25"
              />
            </router-link>
          </div>
          <div
            v-if="recentCases.length > 0"
            class="list-body"
          >
            <div
              v-for="(c, idx) in recentCases"
              :key="'recent-' + c.id"
              class="list-row animate-fade-row"
              :style="{ animationDelay: `${0.5 + idx * 0.06}s` }"
              @click="$router.push(`/cases/details/${c.id}`)"
            >
              <div class="list-row-left">
                <span class="list-row-id">#{{ c.id }}</span>
                <span class="list-row-name">{{ c.debtorName }}</span>
              </div>
              <div class="list-row-right">
                <span class="list-row-amount">{{ formatCurrency(c.remainingAmount) }}</span>
                <b-badge
                  pill
                  :variant="statusVariant(c.status)"
                  class="list-row-badge"
                >
                  {{ statusLabel(c.status) }}
                </b-badge>
              </div>
            </div>
          </div>
          <div
            v-else
            class="list-empty"
          >
            {{ $t('DEBTIA_HOME_NO_CASES_YET') }}
          </div>
        </div>
      </b-col>

      <!-- Top Cases (highest remaining) -->
      <b-col
        lg="6"
      >
        <div
          class="list-card animate-fade-up"
          style="animation-delay: 0.5s"
        >
          <div class="list-card-header">
            <h6 class="list-title">
              <feather-icon
                icon="AlertTriangleIcon"
                size="16"
                class="mr-50"
              />
              {{ $t('DEBTIA_HOME_HIGHEST_DEBT') }}
            </h6>
          </div>
          <div
            v-if="topCases.length > 0"
            class="list-body"
          >
            <div
              v-for="(c, idx) in topCases"
              :key="'top-' + c.id"
              class="list-row animate-fade-row"
              :style="{ animationDelay: `${0.55 + idx * 0.06}s` }"
              @click="$router.push(`/cases/details/${c.id}`)"
            >
              <div class="list-row-left">
                <span class="list-row-id">#{{ c.id }}</span>
                <span class="list-row-name">{{ c.debtorName }}</span>
              </div>
              <div class="list-row-right">
                <span class="list-row-amount text-danger font-weight-bold">{{ formatCurrency(c.remainingAmount) }}</span>
              </div>
              <div class="list-row-progress">
                <div
                  class="progress-fill"
                  :style="{ width: c.principal > 0 ? `${Math.min((c.paidAmount / c.principal) * 100, 100)}%` : '0%' }"
                />
              </div>
            </div>
          </div>
          <div
            v-else
            class="list-empty"
          >
            {{ $t('DEBTIA_HOME_NO_CASES_YET') }}
          </div>
        </div>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BRow, BCol, BAvatar, BBadge,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import axios from '@axios'
import constants from '@/app-configs/constants'

export default {
  components: {
    BRow,
    BCol,
    BAvatar,
    BBadge,
    VueApexCharts,
  },
  data() {
    return {
      loader: true,
      casesData: [],
      defaultCurrency: 'DKK',
      // Animated counters
      animatedTotal: 0,
      animatedActive: 0,
      animatedPending: 0,
      animatedClosed: 0,
      animatedCollected: 0,
    }
  },
  computed: {
    userName() {
      const userData = JSON.parse(localStorage.getItem('userData'))
      return userData && userData.data ? userData.data.name : ''
    },
    todayDate() {
      return new Date().toLocaleDateString('da-DK', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    },
    formattedCases() {
      return this.casesData.map(c => {
        const financials = this.computeFinancials(c.get_documents)
        const mainDebtor = (c.get_debtors && c.get_debtors.length > 0) ? c.get_debtors[0] : null
        return {
          ...c,
          debtorName: mainDebtor ? mainDebtor.name : '-',
          principal: financials.principal,
          paidAmount: financials.paid,
          remainingAmount: financials.remaining,
        }
      })
    },
    activeCasesCount() {
      return this.formattedCases.filter(c => c.status === 1).length
    },
    pendingCasesCount() {
      return this.formattedCases.filter(c => c.status === 0 || c.status === null).length
    },
    closedCasesCount() {
      return this.formattedCases.filter(c => c.status === 2 || c.status === 3).length
    },
    activePausedCases() {
      return this.formattedCases.filter(c => c.status === 1 || c.status === 4)
    },
    activePausedCasesCount() {
      return this.activePausedCases.length
    },
    totalRemaining() {
      return this.activePausedCases.reduce((sum, c) => sum + c.remainingAmount, 0)
    },
    totalCollected() {
      return this.formattedCases.reduce((sum, c) => sum + c.paidAmount, 0)
    },
    kpiCards() {
      return [
        {
          key: 'active', label: this.$t('DEBTIA_ACTIVE_CASES'), icon: 'BriefcaseIcon', color: 'success', animated: this.animatedActive, isCurrency: false,
        },
        {
          key: 'pending', label: this.$t('DEBTIA_HOME_KPI_PENDING'), icon: 'ClockIcon', color: 'warning', animated: this.animatedPending, isCurrency: false,
        },
        {
          key: 'closed', label: this.$t('DEBTIA_HOME_KPI_CLOSED'), icon: 'CheckCircleIcon', color: 'danger', animated: this.animatedClosed, isCurrency: false,
        },
        {
          key: 'collected', label: this.$t('DEBTIA_HOME_KPI_COLLECTED'), icon: 'TrendingUpIcon', color: 'primary', animated: this.animatedCollected, isCurrency: true,
        },
      ]
    },
    recentCases() {
      return [...this.formattedCases]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5)
    },
    topCases() {
      return [...this.formattedCases]
        .filter(c => c.remainingAmount > 0)
        .sort((a, b) => b.remainingAmount - a.remainingAmount)
        .slice(0, 5)
    },

    // Donut chart
    donutSeries() {
      const counts = {
        active: 0, pending: 0, declined: 0, terminated: 0, paused: 0,
      }
      this.formattedCases.forEach(c => {
        if (c.status === 1) counts.active += 1
        else if (c.status === 0 || c.status === null) counts.pending += 1
        else if (c.status === 2) counts.declined += 1
        else if (c.status === 3) counts.terminated += 1
        else if (c.status === 4) counts.paused += 1
      })
      return [counts.active, counts.pending, counts.declined, counts.terminated, counts.paused]
    },
    donutOptions() {
      return {
        chart: { type: 'donut' },
        labels: [this.$t('DEBTIA_STATUS_ACTIVE'), this.$t('DEBTIA_STATUS_PENDING'), this.$t('DEBTIA_STATUS_DECLINED'), this.$t('DEBTIA_STATUS_TERMINATED'), this.$t('DEBTIA_STATUS_PAUSED')],
        colors: ['#088c76', '#ff9f43', '#ea5455', '#b8424d', '#ff9f43'],
        legend: {
          position: 'bottom',
          fontSize: '13px',
          labels: { colors: ['#6b7280'] },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: { fontSize: '14px' },
                value: { fontSize: '22px', fontWeight: 700 },
                total: {
                  show: true,
                  label: this.$t('DEBTIA_HOME_TOTAL_CASES'),
                  fontSize: '12px',
                  fontWeight: 400,
                  formatter: () => `${this.casesData.length}`,
                },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        stroke: { width: 2, colors: ['transparent'] },
        tooltip: {
          y: {
            formatter: val => `${val} ${this.$t('DEBTIA_CASES_LABEL')}`,
          },
        },
      }
    },

    // Area chart (cases per month)
    monthlyCategories() {
      const months = {}
      this.formattedCases.forEach(c => {
        if (!c.created_at) return
        const d = new Date(c.created_at)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        months[key] = (months[key] || 0) + 1
      })
      const sorted = Object.keys(months).sort()
      return sorted.slice(-12)
    },
    areaSeries() {
      const months = {}
      this.formattedCases.forEach(c => {
        if (!c.created_at) return
        const d = new Date(c.created_at)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        months[key] = (months[key] || 0) + 1
      })
      return [{
        name: this.$t('DEBTIA_HOME_NEW_CASES'),
        data: this.monthlyCategories.map(k => months[k] || 0),
      }]
    },
    areaOptions() {
      return {
        chart: {
          toolbar: { show: false },
          sparkline: { enabled: false },
        },
        colors: ['#088c76'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 0.9,
            opacityFrom: 0.5,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        grid: {
          borderColor: 'rgba(0,0,0,0.06)',
          strokeDashArray: 4,
          padding: { left: 10, right: 10 },
        },
        xaxis: {
          categories: this.monthlyCategories.map(k => {
            const [y, m] = k.split('-')
            return new Date(y, m - 1).toLocaleDateString('da-DK', { month: 'short' })
          }),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: '#8a9bb5', fontSize: '12px' } },
        },
        yaxis: {
          labels: {
            style: { colors: '#8a9bb5', fontSize: '12px' },
            formatter: val => Math.round(val),
          },
        },
        tooltip: {
          y: { formatter: val => `${val} ${this.$t('DEBTIA_CASES_LABEL')}` },
        },
      }
    },
  },
  watch: {
    loader: {
      immediate: true,
      handler(val) {
        this.$store.commit('app/SET_GLOBAL_LOADING', { show: val, text: this.$t('DEBTIA_LOADING_DASHBOARD') })
      },
    },
    activeCasesCount(val) { this.animateCounter(val, 'animatedActive') },
    pendingCasesCount(val) { this.animateCounter(val, 'animatedPending') },
    closedCasesCount(val) { this.animateCounter(val, 'animatedClosed') },
    totalRemaining(val) { this.animateCounter(val, 'animatedTotal') },
    totalCollected(val) { this.animateCounter(val, 'animatedCollected') },
  },
  created() {
    this.fetchDashboard()
  },
  beforeDestroy() {
    this.$store.commit('app/SET_GLOBAL_LOADING', false)
  },
  methods: {
    async fetchDashboard() {
      this.loader = true
      try {
        const res = await axios.get(`${constants.baseUrl}/get-debt-cases-list?perPage=10000&page=1&sortDesc=true`)
        if (res.status) {
          this.casesData = res.data.data.data || []
          const currencySetting = await axios.get('/user-settings/get?key=default_currency').catch(() => null)
          if (currencySetting && currencySetting.data && currencySetting.data.data) {
            this.defaultCurrency = currencySetting.data.data.value || 'DKK'
          }
        }
      } catch {
        this.casesData = []
      }
      this.loader = false
    },
    computeFinancials(documents) {
      let principal = 0
      let paid = 0
      if (documents && Array.isArray(documents)) {
        documents.forEach(doc => {
          if (doc.debt_stage === 'base_documents') {
            principal += parseFloat(doc.total_amount) || 0
            paid += parseFloat(doc.paid_amount) || 0
          }
        })
      }
      return { principal, paid, remaining: principal - paid }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: this.defaultCurrency || 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
    },
    animateCounter(target, key) {
      if (!target || target <= 0) {
        this[key] = 0
        return
      }
      const startVal = this[key] || 0
      const duration = 1500
      const startTime = performance.now()
      const animate = currentTime => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - (1 - progress) ** 3
        this[key] = startVal + (target - startVal) * eased
        if (progress < 1) requestAnimationFrame(animate)
        else this[key] = target
      }
      requestAnimationFrame(animate)
    },
    statusVariant(status) {
      if (status === 1) return 'light-success'
      if (status === 2 || status === 3) return 'light-danger'
      if (status === 4) return 'light-warning'
      return 'light-warning'
    },
    statusLabel(status) {
      if (status === 1) return this.$t('DEBTIA_STATUS_ACTIVE')
      if (status === 2) return this.$t('DEBTIA_STATUS_DECLINED')
      if (status === 3) return this.$t('DEBTIA_STATUS_TERMINATED')
      if (status === 4) return this.$t('DEBTIA_STATUS_PAUSED')
      return this.$t('DEBTIA_STATUS_PENDING')
    },
  },
}
</script>

<style lang="scss" scoped>
$debtia-green: #088c76;
$debtia-blue: #002954;
$debtia-blue-light: #0c3860;

// ==============================
// ANIMATIONS
// ==============================
@keyframes fadeSlideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fadeSlideRight {
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes glowPulse {
  0%, 100% { text-shadow: 0 0 8px rgba(255, 255, 255, 0.2), 0 0 24px rgba(8, 140, 118, 0.15); }
  50% { text-shadow: 0 0 16px rgba(255, 255, 255, 0.35), 0 0 40px rgba(8, 140, 118, 0.3); }
}

.animate-fade-up {
  opacity: 0;
  animation: fadeSlideUp 0.5s ease-out forwards;
}

.animate-fade-row {
  opacity: 0;
  animation: fadeSlideRight 0.4s ease-out forwards;
}

// ==============================
// HERO CARD
// ==============================
.hero-card {
  position: relative;
  background:
    radial-gradient(ellipse 150% 80% at 50% 0%, $debtia-green 0%, transparent 50%),
    radial-gradient(ellipse 180% 90% at 0% 50%, $debtia-blue 0%, transparent 50%),
    radial-gradient(ellipse 100% 50% at 0% 100%, $debtia-blue-light 0%, transparent 50%),
    #000000;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 41, 84, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: var(--debtia-spacing);
}

.hero-noise {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: var(--debtia-noise-texture);
  background-size: var(--debtia-noise-size);
  opacity: 0.2;
  pointer-events: none;
  z-index: 0;
}

.hero-inner {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2.5rem;
  gap: 2rem;
  flex-wrap: wrap;
}

.hero-left {
  flex: 1;
  min-width: 200px;
}

.hero-greeting {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: 0.15rem;
}

.hero-name {
  color: #fff;
  font-weight: 700;
  margin-bottom: 0.75rem;
  font-size: 1.4rem;
}

.hero-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.hero-meta-item {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.82rem;
  display: flex;
  align-items: center;
}

.hero-right {
  text-align: right;
  min-width: 180px;
}

.hero-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
  margin-bottom: 0.25rem;
}

.hero-amount {
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  animation: glowPulse 3s ease-in-out infinite;
}

.hero-sublabel {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;
  display: block;
  margin-top: 0.25rem;
}

// ==============================
// DASHBOARD ROWS (uniform --debtia-spacing gap)
// ==============================
.dashboard-row {
  margin-left: calc(var(--debtia-spacing) / -2);
  margin-right: calc(var(--debtia-spacing) / -2);

  > [class*="col"] {
    padding-left: calc(var(--debtia-spacing) / 2) !important;
    padding-right: calc(var(--debtia-spacing) / 2) !important;
    margin-bottom: var(--debtia-spacing);
  }
}

// ==============================
// KPI CARDS
// ==============================
.kpi-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
  }
}

.kpi-value {
  font-weight: 700;
  margin-bottom: 0;
  font-size: 1.35rem;
  line-height: 1.2;
}

.kpi-label {
  color: #7a8b9e;
  font-size: 0.82rem;
}

// ==============================
// CHART CARDS
// ==============================
.chart-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-card-header {
  padding: 1.25rem 1.25rem 0;
}

.chart-title {
  font-weight: 700;
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.chart-card-body {
  padding: 0.75rem;
  flex: 1;
}

.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #8a9bb5;
  font-size: 0.875rem;
  flex: 1;
}

// ==============================
// LIST CARDS
// ==============================
.list-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.list-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.25rem 0.75rem;
}

.list-title {
  font-weight: 700;
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.list-see-all {
  font-size: 0.82rem;
  color: $debtia-green;
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: color 0.15s;

  &:hover {
    color: darken($debtia-green, 8%);
  }
}

.list-body {
  padding: 0 0.75rem 0.75rem;
  flex: 1;
}

.list-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 0.5rem;

  &:hover {
    background: rgba(8, 140, 118, 0.06);
    transform: translateX(3px);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  }
}

.list-row-left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
  flex: 1;
}

.list-row-id {
  color: #8a9bb5;
  font-size: 0.78rem;
  font-weight: 400;
  flex-shrink: 0;
}

.list-row-name {
  font-weight: 400;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-row-right {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.list-row-amount {
  font-weight: 700;
  font-size: 0.85rem;
  white-space: nowrap;
}

.list-row-badge {
  font-size: 0.7rem;
}

.list-row-progress {
  width: 100%;
  height: 3px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.15rem;

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, $debtia-green, #4fd1c5);
    border-radius: 2px;
    transition: width 1s ease-out;
  }
}

.list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #8a9bb5;
  font-size: 0.875rem;
  flex: 1;
}
</style>

<style lang="scss">
// Dark mode overrides (unscoped)
.dark-layout #dashboard {
  .kpi-card {
    background: rgba(10, 30, 50, 0.7);
    border-color: rgba(8, 140, 118, 0.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

    &:hover {
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.3);
    }

    .kpi-label {
      color: #6b8299;
    }
  }

  .chart-card,
  .list-card {
    background: rgba(10, 30, 50, 0.7);
    border-color: rgba(8, 140, 118, 0.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }

  .chart-empty {
    color: #4a6580;
  }

  .list-row {
    &:hover {
      background: rgba(8, 140, 118, 0.08);
    }

    &:not(:last-child) {
      border-bottom-color: rgba(255, 255, 255, 0.05);
    }
  }

  .list-row-id {
    color: #4a6580;
  }

  .list-row-name {
    color: #e2e8f0;
  }

  .list-empty {
    color: #4a6580;
  }

  .list-see-all {
    color: #4fd1c5;
  }

  .list-row-progress {
    background: rgba(255, 255, 255, 0.06);
  }
}

// ApexCharts dark mode
.dark-layout .apexcharts-text {
  fill: #6b8299 !important;
}

.dark-layout .apexcharts-legend-text {
  color: #6b8299 !important;
}

.dark-layout .apexcharts-gridline {
  stroke: rgba(255, 255, 255, 0.06) !important;
}

.dark-layout .apexcharts-datalabel-label {
  fill: #6b8299 !important;
}

.dark-layout .apexcharts-datalabel-value {
  fill: #e2e8f0 !important;
}
</style>
